/**
 * TransferHistoryTable Component
 * Displays a table of past transfers with status badges.
 */

import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import type { TransferPending } from '@/types/collection.types';

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(value);

const formatDate = (dateStr: string) =>
  new Date(dateStr + 'T12:00:00').toLocaleDateString('es-MX', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

interface TransferHistoryTableProps {
  transfers: TransferPending[];
  isLoading: boolean;
}

function StatusBadge({ status }: { status: string }) {
  const variant = status === 'PENDING' ? 'warning' : 'success';
  const label = status === 'PENDING' ? 'Pendiente' : 'Completada';
  return <Badge variant={variant}>{label}</Badge>;
}

export function TransferHistoryTable({ transfers, isLoading }: TransferHistoryTableProps) {
  if (isLoading) {
    return (
      <Card title="Historial de Transferencias">
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} width="100%" height={40} variant="rectangular" />
          ))}
        </div>
      </Card>
    );
  }

  return (
    <Card title="Historial de Transferencias">
      {(transfers ?? []).length === 0 ? (
        <p className="py-8 text-center text-sm text-muted-foreground">
          No hay transferencias en el historial.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                <th className="px-3 py-3">Periodo</th>
                <th className="px-3 py-3 text-right">Principal</th>
                <th className="hidden px-3 py-3 text-right md:table-cell">Interes UNE-Credito</th>
                <th className="hidden px-3 py-3 text-right md:table-cell">Interes Fiado</th>
                <th className="px-3 py-3 text-right">Total Transferido</th>
                <th className="px-3 py-3 text-center">Registros</th>
                <th className="px-3 py-3 text-center">Estado</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {(transfers ?? []).map((t) => (
                <tr
                  key={t.transfer_id}
                  className="transition-colors hover:bg-muted/40"
                >
                  <td className="whitespace-nowrap px-3 py-3 font-medium text-foreground">
                    {formatDate(t.period_start)} - {formatDate(t.period_end)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-right text-foreground">
                    {formatCurrency(t.total_principal_mxn)}
                  </td>
                  <td className="hidden whitespace-nowrap px-3 py-3 text-right text-foreground md:table-cell">
                    {formatCurrency(t.total_partner_interest_mxn)}
                  </td>
                  <td className="hidden whitespace-nowrap px-3 py-3 text-right text-foreground md:table-cell">
                    {formatCurrency(t.total_fiado_interest_mxn)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-right font-semibold text-foreground">
                    {formatCurrency(t.total_amount_mxn)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-center text-foreground">
                    {t.records_count}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-center">
                    <StatusBadge status={t.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
